import { BrowserActivityDetails } from './BrowserActivityDetails';
import { X } from 'lucide-react';
import type { Topology } from '@/services/observability-api';
import { nodePresentation } from './node-presentation';
import { cn } from '@/lib/utils';
import { edgeActivityLabel, formatPercent, formatRequestRate } from './graph-utils';
import type { SelectedTopologyItem } from './types';
import {
  DestinationIdentityDetails,
  type DestinationIdentityDetailsProps,
} from './DestinationIdentityDetails';

export interface ArchitectureDetailsDrawerProps {
  topology: Topology;
  selected: SelectedTopologyItem;
  onClose: () => void;
  onSelectNode: (nodeId: string) => void;
  floating?: boolean;
  identity?: DestinationIdentityDetailsProps;
}

const healthClasses = {
  healthy: 'bg-green-100 text-green-800',
  degraded: 'bg-amber-100 text-amber-800',
  unhealthy: 'bg-red-100 text-red-800',
  unknown: 'bg-neutral-100 text-neutral-700',
};

export function ArchitectureDetailsDrawer({
  topology,
  selected,
  onClose,
  onSelectNode,
  floating = false,
  identity,
}: ArchitectureDetailsDrawerProps) {
  if (!selected) return null;
  const node =
    selected.type === 'node' ? topology.nodes.find((item) => item.id === selected.id) : undefined;
  const edge =
    selected.type === 'edge' ? topology.edges.find((item) => item.id === selected.id) : undefined;
  if (!node && !edge) return null;
  const connectedEdges = node
    ? topology.edges.filter((item) => item.source === node.id || item.target === node.id)
    : [];
  const nodeFor = (id: string) => topology.nodes.find((item) => item.id === id);
  const connectionNode = (id: string) => {
    const connectedNode = nodeFor(id);

    if (!connectedNode) return <span>{id}</span>;

    return (
      <button
        type="button"
        onClick={() => onSelectNode(id)}
        className="rounded font-medium text-neutral-700 underline-offset-2 hover:text-lavender-700 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lavender-500"
        aria-label={`View ${connectedNode.label}`}
      >
        {connectedNode.label}
      </button>
    );
  };

  return (
    <aside
      className={cn(
        'w-full shrink-0 border-t border-neutral-200 bg-white p-4 lg:w-80 lg:border-l lg:border-t-0',
        floating &&
          'absolute inset-4 z-20 w-auto overflow-y-auto rounded-lg border shadow-elevated lg:left-auto lg:top-32 lg:w-80'
      )}
      aria-label="Selected architecture item details"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-neutral-500">
            {node ? nodePresentation(node).label : edge?.protocol}
          </p>
          <h2 className="mt-1 text-base font-semibold text-neutral-900">
            {node ? (
              node.label
            ) : (
              <>
                {connectionNode(edge!.source)} <span aria-hidden="true">→</span>{' '}
                {connectionNode(edge!.target)}
              </>
            )}
          </h2>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="rounded p-1 text-neutral-500 hover:bg-neutral-100"
          aria-label="Close details"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
      {node?.id === 'user' && (
        <p className="mt-3 text-sm text-neutral-600">
          Incoming traffic without an identified traced caller. This is not a Nava service.
        </p>
      )}
      {node?.evidence === 'configured' && (
        <p className="mt-3 text-sm text-neutral-600">
          Configured client connection. No observed traffic in this time range.
        </p>
      )}
      {node ? (
        <>
          <span
            className={cn(
              'mt-3 inline-flex rounded-full px-2 py-1 text-xs font-semibold',
              healthClasses[node.health]
            )}
          >
            {node.health}
          </span>
          <dl className="mt-4 grid grid-cols-2 gap-3 text-sm">
            <div>
              <dt className="text-neutral-500">Traffic</dt>
              <dd className="font-semibold text-neutral-900">
                {formatRequestRate(node.requestRate)}
              </dd>
            </div>
            <div>
              <dt className="text-neutral-500">Errors</dt>
              <dd className="font-semibold text-neutral-900">{formatPercent(node.errorRate)}</dd>
            </div>
            <div>
              <dt className="text-neutral-500">p95 latency</dt>
              <dd className="font-semibold text-neutral-900">{node.p95LatencyMs.toFixed(0)} ms</dd>
            </div>
            <div>
              <dt className="text-neutral-500">Connections</dt>
              <dd className="font-semibold text-neutral-900">{connectedEdges.length}</dd>
            </div>
          </dl>
          {node.kind === 'external' && node.id.startsWith('unresolved:') && identity && (
            <DestinationIdentityDetails {...identity} />
          )}
          <h3 className="mt-6 text-sm font-semibold text-neutral-900">Connections</h3>
          <ul className="mt-2 space-y-2 text-sm">
            {connectedEdges.map((item) => (
              <li key={item.id} className="rounded bg-neutral-50 p-2 text-neutral-700">
                {connectionNode(item.source)} <span aria-hidden="true">→</span>{' '}
                {connectionNode(item.target)}
                <span className="block text-xs text-neutral-500">
                  {item.protocol} · {edgeActivityLabel(item)}
                </span>
              </li>
            ))}
          </ul>
        </>
      ) : edge?.evidence === 'configured' ? (
        <p className="mt-5 text-sm text-neutral-600">
          No observed traffic in this time range. This connection is configured, not measured.
        </p>
      ) : edge && ['beacon', 'websocket', 'webrtc'].includes(edge.protocol) ? (
        <BrowserActivityDetails edge={edge} />
      ) : edge ? (
        <dl className="mt-5 space-y-4 text-sm">
          <div>
            <dt className="text-neutral-500">
              {edge.trafficType === 'telemetry' ? 'Telemetry uploads' : 'Traffic'}
            </dt>
            <dd className="font-semibold text-neutral-900">
              {formatRequestRate(edge.requestRate)}
            </dd>
          </div>
          <div>
            <dt className="text-neutral-500">Errors</dt>
            <dd className="font-semibold text-neutral-900">{formatPercent(edge.errorRate)}</dd>
          </div>
          <div>
            <dt className="text-neutral-500">p95 latency</dt>
            <dd className="font-semibold text-neutral-900">{edge.p95LatencyMs.toFixed(0)} ms</dd>
          </div>
        </dl>
      ) : null}
    </aside>
  );
}
